import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type {TFunction} from 'i18next';
import React from 'react';
import {useTranslation} from 'react-i18next';

import type {WatchedTestRun} from '../hooks/useTestRunWatch';
import {testRunSucceeded} from '../utils/testRunState';
import type {FailureReason, NothingSent, TestRunState, TestRunSubject} from '../utils/testRunState';
import CanvasStrip from './CanvasStrip';

interface TestRunBannerProps {
    run: WatchedTestRun;
    skipping: boolean;
    skipFailed: boolean;
    onSkipWait: () => void;
    onViewRun: (executionId: string) => void;
    onClose: () => void;
}

const FAILURE_KEYS: Record<FailureReason, string> = {
    'no-recipients': 'Nobody to send to. Check who this email goes to.',
    'no-survey': 'No survey is chosen in this step.',
    'no-audience': 'No audience is chosen in this step.',
    'audience-gone': 'This audience no longer exists.',
    other: 'This step could not run. View run shows why.',
};

const runningBody = (subject: TestRunSubject, t: TFunction): string => {
    const {address, memberCount, memberName, team} = subject;

    if (memberCount === 1 && memberName) {
        return team
            ? t('with {{name}} · Emails go to {{address}}, not to the team.', {name: memberName, address})
            : t('with {{name}} · Emails go to {{address}}, not to the guest.', {name: memberName, address});
    }
    if (memberCount) {
        return team
            ? t('Test run with team members', {count: memberCount, address})
            : t('Test run with guests', {count: memberCount, address});
    }
    return team
        ? t('Emails go to {{address}}, not to the team.', {address})
        : t('Emails go to {{address}}, not to the guest.', {address});
};

const nothingSentBody = (nothing: NothingSent, team: boolean, t: TFunction): string => {
    const someone = team ? t('the team member') : t('the guest');

    switch (nothing.kind) {
        case 'no-recipient':
            return t('No email sent: “{{step}}” has no recipient set.', {step: nothing.step});
        case 'no-address':
            if (nothing.count > 1) {
                return team
                    ? t('No email sent: team members without an email address', {count: nothing.count})
                    : t('No email sent: guests without an email address', {count: nothing.count});
            }
            return team
                ? t('No email sent: {{name}} has no email address. Choose a team member with an address to see the email.', {name: nothing.name || someone})
                : t('No email sent: {{name}} has no email address. Choose a guest with an address to see the email.', {name: nothing.name || someone});
        case 'condition':
            return t('No email sent: {{name}} does not meet “{{condition}}”.', {name: nothing.name || someone, condition: nothing.condition});
        case 'left-audience':
            if (nothing.count > 1 || !nothing.name) {
                return team
                    ? t('No email sent: the chosen team members are no longer in “{{audience}}”.', {audience: nothing.audience})
                    : t('No email sent: the chosen guests are no longer in “{{audience}}”.', {audience: nothing.audience});
            }
            return t('No email sent: {{name}} is no longer in “{{audience}}”.', {name: nothing.name, audience: nothing.audience});
        case 'empty-audience':
            return t('No email sent: nobody is in “{{audience}}” right now.', {audience: nothing.audience});
        case 'push-only':
            return t('Push messages are not sent in a test, so nothing was delivered.');
        default:
            return t('No email was sent. The outlined steps show the way the test took.');
    }
};

const finishedBody = (state: TestRunState, subject: TestRunSubject, t: TFunction): string => {
    const team = !!subject.team;
    if (!state.emails) return nothingSentBody(state.nothingSent ?? {kind: 'none'}, team, t);

    return [
        t('Test run emails on their way', {count: state.emails, address: subject.address}),
        state.skipped
            ? team
                ? t('Test run team members skipped', {count: state.skipped})
                : t('Test run guests skipped', {count: state.skipped})
            : '',
        state.waitSkipped ? t('Steps after the skipped wait were checked as of now.') : '',
        t('Check your inbox.'),
    ].filter(Boolean).join(' ');
};

const failedBody = (state: TestRunState, subject: TestRunSubject, t: TFunction): string => {
    const failure = state.failure!;
    const reason = t(FAILURE_KEYS[failure.reason]);

    if (!failure.stepName) return reason;
    // The test's own run failed (no member runs, or a step below "Wait for everyone"): one run, one sentence.
    if (state.memberRuns <= 1 || !failure.failedRuns) return t('At “{{step}}”: {{reason}}', {step: failure.stepName, reason});

    const values = {failed: failure.failedRuns, total: state.memberRuns, step: failure.stepName, reason};
    return [
        subject.team
            ? t('For {{failed}} of {{total}} team members at “{{step}}”: {{reason}}', values)
            : t('For {{failed}} of {{total}} guests at “{{step}}”: {{reason}}', values),
        state.emails ? t('Test run other emails on their way', {count: state.emails}) : '',
    ].filter(Boolean).join(' ');
};

/**
 * The test run's orange CanvasStrip: one sentence that stays until Close (or Esc, Preview, a new test).
 * Progress is this strip plus still outlines on passed cards (`TestRunTrailContext`), never toasts or a pulse.
 * Orange while it runs, green once it finished with emails sent, red when it failed.
 */
const TestRunBanner = ({run, skipping, skipFailed, onSkipWait, onViewRun, onClose}: TestRunBannerProps) => {
    const {t, i18n} = useTranslation();
    const {state, subject, slow} = run;
    // Skip the wait was pressed: the test runs again, said before the next answer shows it.
    const phase = state.phase === 'parked' && skipping ? 'running' : state.phase;

    let lead = t('Test run');
    let body: string;
    let icon: React.ReactNode;
    let actions: 'none' | 'view' | 'parked' = 'view';

    switch (phase) {
        case 'parked': {
            const when = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
                weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
            }).format(state.parked!.until);
            body = [
                t('Waiting at “{{step}}” until {{when}}. Then the rest goes to {{address}}.', {
                    step: state.parked!.waitName, when, address: subject.address
                }),
                skipFailed ? t('The wait could not be skipped.') : '',
            ].filter(Boolean).join(' ');
            icon = <AccessTimeRoundedIcon/>;
            actions = 'parked';
            break;
        }
        case 'finished':
            lead = t('Test run finished');
            body = finishedBody(state, subject, t);
            icon = state.emails ? <CheckCircleOutlineRoundedIcon/> : <WarningAmberRoundedIcon/>;
            break;
        case 'failed':
            lead = t('Test run failed');
            body = failedBody(state, subject, t);
            icon = <ErrorOutlineRoundedIcon/>;
            break;
        case 'cancelled':
            lead = t('Test run cancelled');
            body = t('The automation was deleted.');
            icon = <WarningAmberRoundedIcon/>;
            break;
        default:
            body = slow ? t('Still running. It finishes on its own; the result will be under Runs.') : runningBody(subject, t);
            icon = <CircularProgress size={16} thickness={5} color={'inherit'}/>;
            actions = slow ? 'view' : 'none';
    }

    const color = phase === 'failed' ? 'error' : phase === 'finished' && testRunSucceeded(state) ? 'success' : 'warning';

    return (
        <CanvasStrip
            tone={color}
            icon={icon}
            title={`${lead} · ${body}`}
            message={(
                <>
                    <Box component={'span'} sx={{fontWeight: 600}}>{lead}</Box>
                    {' · '}
                    {body}
                </>
            )}
            busy={phase === 'running'}
            actions={actions === 'none' ? undefined : (
                <>
                    {actions === 'parked' ? (
                        <Button size={'small'} color={color} onClick={onSkipWait} disabled={skipping} sx={{flexShrink: 0, whiteSpace: 'nowrap'}}>
                            {t('Skip the wait')}
                        </Button>
                    ) : (
                        <Button
                            size={'small'}
                            color={color}
                            onClick={() => onViewRun(state.viewExecutionId ?? run.rootId)}
                            sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                        >
                            {t('View run')}
                        </Button>
                    )}
                    <Button size={'small'} color={color} onClick={onClose} sx={{flexShrink: 0, whiteSpace: 'nowrap'}}>
                        {t('Close')}
                    </Button>
                </>
            )}
        />
    );
};

export default TestRunBanner;
